// Geo helpers for the map screens: ISO-3166-1 alpha-2 → display name / flag
// emoji, and the equirectangular projection onto the stylized dot-map.

const ISO_RE = /^[A-Za-z]{2}$/;

// Intl.DisplayNames may be missing in older Telegram WebViews.
const regionNames = (() => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" });
  } catch {
    return null;
  }
})();

/** English country name for an ISO alpha-2 code; falls back to the raw code. */
export function countryName(code: string): string {
  const cc = code.toUpperCase();
  if (!ISO_RE.test(cc) || !regionNames) return cc;
  try {
    return regionNames.of(cc) ?? cc;
  } catch {
    return cc;
  }
}

/** Flag emoji for an ISO alpha-2 code, via regional-indicator pairs. */
export function flagEmoji(code: string): string {
  if (!ISO_RE.test(code)) return "🏳";
  const cc = code.toUpperCase();
  return String.fromCodePoint(
    0x1f1e6 + cc.charCodeAt(0) - 65,
    0x1f1e6 + cc.charCodeAt(1) - 65
  );
}

/** "Lisbon" when the city is known, otherwise the country name. */
export function locationLabel(loc: { city: string | null; countryCode: string }): string {
  return loc.city ?? countryName(loc.countryCode);
}

// Viewport of the WorldDotMap dot grid. The mask is a stylized silhouette, not
// a real projection: latTop/latBottom are its crop (Arctic trimmed, no
// Antarctica), calibrated so real coordinates land on the right continents.
export const GEO_VIEW = { w: 360, h: 150, latTop: 74, latBottom: -62 } as const;

/** Equirectangular lat/lng → dot-map viewport coordinates (clamped). */
export function projectToMap(lat: number, lng: number): { x: number; y: number } {
  const x = ((lng + 180) / 360) * GEO_VIEW.w;
  const y = ((GEO_VIEW.latTop - lat) / (GEO_VIEW.latTop - GEO_VIEW.latBottom)) * GEO_VIEW.h;
  return {
    x: Math.min(GEO_VIEW.w, Math.max(0, x)),
    y: Math.min(GEO_VIEW.h, Math.max(0, y)),
  };
}
